import React, { useState } from 'react';
import { MoreHorizontal, Lock, Users, Globe, Trash2, Eye, X } from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from './ui/sheet';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * PhotoTile - Single photo with owner controls
 * 
 * Features:
 * - Displays photo thumbnail
 * - Owner can change visibility or delete
 * - Uses mobile-first bottom sheet UI (no hover dependency)
 * 
 * Modal copy (LOCKED):
 * - "Photo options"
 * - "Change visibility"
 * - "Remove photo"
 * - "Cancel"
 * - Confirm: "Remove photo?" + "This can't be undone." + "Remove / Cancel"
 */
export default function PhotoTile({ photo, isOwner, onUpdate, onDelete, circles = [] }) {
  const [showOptionsSheet, setShowOptionsSheet] = useState(false);
  const [showVisibilitySheet, setShowVisibilitySheet] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [newVisibility, setNewVisibility] = useState(photo.visibility_type || 'private');
  const [selectedCircles, setSelectedCircles] = useState(photo.circle_ids || []);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const getVisibilityIcon = () => {
    const vis = photo.visibility_type || 'private';
    switch (vis) {
      case 'public':
        return <Globe className="w-3 h-3 text-purple-500" />;
      case 'circle':
        return <Users className="w-3 h-3 text-blue-500" />;
      default:
        return <Lock className="w-3 h-3 text-green-500" />;
    }
  };

  const handleCircleToggle = (circleId) => {
    setSelectedCircles(prev => 
      prev.includes(circleId) 
        ? prev.filter(id => id !== circleId)
        : [...prev, circleId]
    );
  };

  const handleSaveVisibility = async () => {
    if (newVisibility === 'circle' && selectedCircles.length === 0) {
      return; // Don't save if circle selected but no circles chosen
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const photoId = photo.photo_id || photo.id;
      
      const res = await fetch(`${API_URL}/api/photos/${photoId}/visibility`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          visibility_type: newVisibility,
          circle_ids: newVisibility === 'circle' ? selectedCircles : []
        })
      });

      if (res.ok) {
        onUpdate?.({
          ...photo,
          visibility_type: newVisibility,
          circle_ids: newVisibility === 'circle' ? selectedCircles : []
        });
        setShowVisibilitySheet(false);
      }
    } catch (err) {
      console.error('Error updating visibility:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const token = localStorage.getItem('token');
      const photoId = photo.photo_id || photo.id;
      
      const res = await fetch(`${API_URL}/api/photos/${photoId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (res.ok) {
        onDelete?.(photoId);
        setShowDeleteDialog(false);
        toast.success('Photo removed');
      } else {
        toast.error('Failed to remove photo');
      }
    } catch (err) {
      console.error('Error deleting photo:', err);
      toast.error('Failed to remove photo');
    } finally {
      setDeleting(false);
    }
  };

  const photoUrl = photo.url?.startsWith('/') ? `${API_URL}${photo.url}` : photo.url;

  return (
    <>
      <div className="relative aspect-square rounded-lg overflow-hidden group">
        <img
          src={photoUrl}
          alt=""
          className="w-full h-full object-cover"
          loading="lazy"
        />
        
        {/* Visibility indicator */}
        <div className="absolute bottom-2 left-2 p-1 rounded bg-black/50">
          {getVisibilityIcon()}
        </div>
        
        {/* Owner controls - always visible on mobile, hover on desktop */}
        {isOwner && (
          <button
            onClick={() => setShowOptionsSheet(true)}
            className="absolute top-2 right-2 p-2 rounded-full bg-black/60 text-white 
                       sm:opacity-0 sm:group-hover:opacity-100 transition-opacity
                       active:bg-black/80 touch-manipulation"
            aria-label="Photo options"
          >
            <MoreHorizontal className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Photo Options Bottom Sheet (Mobile-first) */}
      <Sheet open={showOptionsSheet} onOpenChange={setShowOptionsSheet}>
        <SheetContent side="bottom" className="rounded-t-2xl pb-safe">
          <SheetHeader className="pb-4">
            <SheetTitle className="text-center">Photo options</SheetTitle>
          </SheetHeader>

          <div className="space-y-2 pb-4">
            {/* Change Visibility Button */}
            <button
              onClick={() => {
                setShowOptionsSheet(false);
                setTimeout(() => setShowVisibilitySheet(true), 150);
              }}
              className="w-full flex items-center gap-4 p-4 rounded-xl bg-muted hover:bg-muted/80 
                         active:bg-muted/60 transition-colors text-left"
            >
              <Eye className="w-5 h-5 text-muted-foreground" />
              <span className="text-base font-medium">Change visibility</span>
            </button>

            {/* Remove Photo Button */}
            <button
              onClick={() => {
                setShowOptionsSheet(false);
                setTimeout(() => setShowDeleteDialog(true), 150);
              }}
              className="w-full flex items-center gap-4 p-4 rounded-xl bg-red-50 dark:bg-red-950/30 
                         hover:bg-red-100 dark:hover:bg-red-950/50 active:bg-red-200 dark:active:bg-red-950/70 
                         transition-colors text-left text-red-600 dark:text-red-400"
            >
              <Trash2 className="w-5 h-5" />
              <span className="text-base font-medium">Remove photo</span>
            </button>
          </div>

          {/* Cancel Button */}
          <Button 
            variant="outline" 
            onClick={() => setShowOptionsSheet(false)}
            className="w-full h-12 text-base"
          >
            Cancel
          </Button>
        </SheetContent>
      </Sheet>

      {/* Change Visibility Sheet (Bottom Sheet) */}
      <Sheet open={showVisibilitySheet} onOpenChange={setShowVisibilitySheet}>
        <SheetContent side="bottom" className="h-auto max-h-[85vh] overflow-y-auto rounded-t-2xl pb-safe">
          <SheetHeader className="pb-2">
            <SheetTitle className="text-center">Change visibility</SheetTitle>
          </SheetHeader>

          <div className="py-4 space-y-3">
            {/* Private */}
            <button 
              onClick={() => {
                setNewVisibility('private');
                setSelectedCircles([]);
              }}
              className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 text-left transition-all ${
                newVisibility === 'private' 
                  ? 'border-green-500 bg-green-50 dark:bg-green-950/30' 
                  : 'border-transparent bg-muted'
              }`}
            >
              <Lock className={`w-6 h-6 ${newVisibility === 'private' ? 'text-green-500' : 'text-muted-foreground'}`} />
              <div className="flex-1">
                <div className="font-medium text-foreground">Private</div>
                <div className="text-sm text-muted-foreground">Only you can see</div>
              </div>
              {newVisibility === 'private' && (
                <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              )}
            </button>
            
            {/* Circle */}
            <button 
              onClick={() => setNewVisibility('circle')}
              className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 text-left transition-all ${
                newVisibility === 'circle' 
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/30' 
                  : 'border-transparent bg-muted'
              }`}
            >
              <Users className={`w-6 h-6 ${newVisibility === 'circle' ? 'text-blue-500' : 'text-muted-foreground'}`} />
              <div className="flex-1">
                <div className="font-medium text-foreground">Circle</div>
                <div className="text-sm text-muted-foreground">Share with specific circles</div>
              </div>
              {newVisibility === 'circle' && (
                <div className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center">
                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              )}
            </button>
            
            {/* Public */}
            <button 
              onClick={() => {
                setNewVisibility('public');
                setSelectedCircles([]);
              }}
              className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 text-left transition-all ${
                newVisibility === 'public' 
                  ? 'border-purple-500 bg-purple-50 dark:bg-purple-950/30' 
                  : 'border-transparent bg-muted'
              }`}
            >
              <Globe className={`w-6 h-6 ${newVisibility === 'public' ? 'text-purple-500' : 'text-muted-foreground'}`} />
              <div className="flex-1">
                <div className="font-medium text-foreground">Public</div>
                <div className="text-sm text-muted-foreground">Visible to connections</div>
              </div>
              {newVisibility === 'public' && (
                <div className="w-5 h-5 rounded-full bg-purple-500 flex items-center justify-center">
                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              )}
            </button>

            {/* Circle Selection */}
            {newVisibility === 'circle' && (
              <div className="mt-2 p-4 rounded-xl bg-muted/50">
                <div className="text-sm font-medium mb-3 text-foreground">
                  Choose circles
                </div>
                
                {circles.length === 0 ? (
                  <div className="p-4 rounded-xl text-center bg-background">
                    <Users className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">No circles yet</p>
                    <a href="/circles" className="inline-block mt-2 text-sm font-medium text-blue-500">
                      Create Circle →
                    </a>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {circles.map(circle => (
                      <button
                        key={circle.id}
                        onClick={() => handleCircleToggle(circle.id)}
                        className={`w-full flex items-center gap-3 p-3 rounded-xl text-left transition-all ${
                          selectedCircles.includes(circle.id) 
                            ? 'bg-blue-100 dark:bg-blue-900/30' 
                            : 'bg-background hover:bg-muted'
                        }`}
                      >
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                          selectedCircles.includes(circle.id) 
                            ? 'border-blue-500 bg-blue-500' 
                            : 'border-muted-foreground'
                        }`}>
                          {selectedCircles.includes(circle.id) && (
                            <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                        <span className="text-foreground">{circle.name}</span>
                      </button>
                    ))}
                  </div>
                )}
                
                {circles.length > 0 && selectedCircles.length === 0 && (
                  <p className="mt-3 text-sm text-red-500">
                    Please select at least one circle
                  </p>
                )}
              </div>
            )}
          </div>

          <SheetFooter className="gap-3 pt-2">
            <Button 
              variant="outline" 
              onClick={() => setShowVisibilitySheet(false)}
              className="flex-1 h-12 text-base"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSaveVisibility} 
              disabled={saving || (newVisibility === 'circle' && selectedCircles.length === 0)}
              className="flex-1 h-12 text-base"
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Delete Confirmation Dialog (LOCKED COPY) */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="sm:max-w-[400px] mx-4">
          <DialogHeader>
            <DialogTitle>Remove photo?</DialogTitle>
            <DialogDescription>
              This can&apos;t be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex flex-col-reverse sm:flex-row gap-3 sm:gap-2 pt-2">
            <Button 
              variant="outline" 
              onClick={() => setShowDeleteDialog(false)}
              className="w-full sm:w-auto h-12 sm:h-10 text-base sm:text-sm"
            >
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDelete}
              disabled={deleting}
              className="w-full sm:w-auto h-12 sm:h-10 text-base sm:text-sm"
            >
              {deleting ? 'Removing...' : 'Remove'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
